import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { useNews } from '../context/NewsContext';
import * as api from '../services/api';
import {
    CONTENT_CHANNEL_DESKTOP,
    CONTENT_CHANNEL_MOBILE,
    normalizeContentChannel
} from '../shared/channel/contentChannel';

// DOMAIN COMPONENTS
import NewsReaderLayout from '../components/news/NewsReaderLayout';
import NewsGallery from '../components/news/NewsGallery';
import NewsHeader from '../components/news/NewsHeader';
import NewsContent from '../components/news/NewsContent';
import NewsNotFound from '../components/news/NewsNotFound';

const isVideoSource = (value = '') => {
    const v = String(value || '').toLowerCase();
    return /\.(mp4|webm|ogg)(\?.*)?$/i.test(v) || v.includes('youtube') || v.includes('vimeo');
};

const resolveGalleryUrl = (entry) => {
    if (!entry) return '';
    if (typeof entry === 'string') return entry;
    return entry.original || entry.url || entry.src || '';
};

/**
 * NEWS PAGE v2.0 (GOVERNANCE COMPLIANT)
 * Refactored to eliminate direct MUI usage and enforce separation of concerns.
 * - Layout: NewsReaderLayout
 * - Components: NewsHeader, NewsGallery, NewsContent, NewsShareWidget
 */
const NewsPage = () => {
    const { id } = useParams();
    const location = useLocation();
    const { getNewsById, loading } = useNews();
    const storyFromContext = getNewsById(id);
    const [resolvedStory, setResolvedStory] = useState(storyFromContext || null);
    const [resolvingStory, setResolvingStory] = useState(false);

    const storyId = useMemo(() => Number.parseInt(id, 10), [id]);

    useEffect(() => {
        setResolvedStory(storyFromContext || null);
    }, [storyFromContext, id]);

    useEffect(() => {
        let isMounted = true;

        const resolveCrossChannelStory = async () => {
            if (storyFromContext || !Number.isFinite(storyId)) return;

            const queryChannel = normalizeContentChannel(
                new URLSearchParams(location.search || '').get('channel')
            );
            const channelsToTry = [queryChannel, CONTENT_CHANNEL_DESKTOP, CONTENT_CHANNEL_MOBILE]
                .filter(Boolean)
                .filter((value, index, array) => array.indexOf(value) === index);

            setResolvingStory(true);
            try {
                for (const channel of channelsToTry) {
                    const items = await api.getNews(channel);
                    const found = Array.isArray(items)
                        ? items.find((item) => item?.id === storyId)
                        : null;
                    if (found) {
                        if (isMounted) setResolvedStory(found);
                        return;
                    }
                }
            } catch (error) {
                console.error('Falha ao resolver notícia em outro canal:', error);
            } finally {
                if (isMounted) setResolvingStory(false);
            }
        };

        resolveCrossChannelStory();
        return () => {
            isMounted = false;
        };
    }, [storyFromContext, storyId, location.search]);

    const story = storyFromContext || resolvedStory;

    if (!story) {
        if (loading || resolvingStory) {
            return null;
        }
        return <NewsNotFound />;
    }

    // Hero: single primary media (image or video). Bottom: full event gallery/video carousel.
    const primaryUrl =
        story.image ||
        (Array.isArray(story.gallery) && story.gallery.length
            ? resolveGalleryUrl(story.gallery[0])
            : '');

    const heroSingle = primaryUrl
        ? [{
            original: primaryUrl,
            thumbnail: primaryUrl,
            isVideo: isVideoSource(primaryUrl),
        }]
        : [];

    const shareUrl = typeof window !== 'undefined'
        ? `${window.location.origin}/news/${story.id}`
        : `/news/${story.id}`;

		    return (
		        <NewsReaderLayout>
            {/* Top: hero only (single media: image/video). */}
            <NewsGallery variant="hero" gallery={heroSingle} image={primaryUrl} title={story.title} />

            <NewsHeader 
                title={story.title} 
                subtitle={story.subtitle}
                author={story.author} 
                publishedAt={story.publishedAt} 
                shareUrl={shareUrl}
            />

	            <NewsContent 
	                content={story.content} 
	            />

            {/* Bottom: full media carousel (images/videos) */}
            <NewsGallery variant="bottom" gallery={story.gallery} image={story.image} title={story.title} />
	        </NewsReaderLayout>
	    );
	};

export default NewsPage;
